import React, { useState } from 'react'
import { Menu as MuiMenu, MenuItem } from '@mui/material'
import AddTestModal from '../../components/AddTestModal'
import EdSwitch from '@/components/forms/EdSwitch'
import { motion, AnimatePresence } from 'framer-motion'

interface Test {
  id_test?: string;
  nome?: string;
  descrizione?: string;
  data?: string;
  categoria?: string;
  pubblicato?: string;
}

interface TestSectionProps {
  isReadOnly?: boolean;
  data?: Test[];
}

const TestSection = ({ isReadOnly = false, data = [] }: TestSectionProps) => {
  const [activeTab, setActiveTab] = useState('esercitazioni')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create')
  const [selectedTestData, setSelectedTestData] = useState<Test | null>(null)
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedTestId, setSelectedTestId] = useState<string | null>(null)
  const [modalInitialTab, setModalInitialTab] = useState<number>(0)

  const testCategories = [
    { id: 'esercitazioni', label: 'Esercitazioni' },
    { id: 'prove-intercorso', label: 'Prove intercorso' },
    { id: 'test-fine-corso', label: 'Test fine corso' },
    { id: 'test-gradimento', label: 'Test di gradimento' }
  ]

  // Filtra i test in base alla categoria attiva
  const filteredTests = data.filter(test => test.categoria === activeTab)

  // Conta per categoria
  const getCategoryCount = (categoryId: string) => {
    return data.filter(test => test.categoria === categoryId).length
  }

  const handleEditTest = (test: Test) => {
    setSelectedTestData(test)
    setModalMode('edit')
    setModalInitialTab(0)
    setIsModalOpen(true)
  }

  const handleCreateTest = () => {
    setSelectedTestData(null)
    setModalMode('create')
    setModalInitialTab(0)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedTestData(null)
    setModalMode('create')
    setModalInitialTab(0)
  }

  const handleActionMenuOpen = (event: React.MouseEvent<HTMLElement>, testId: string) => {
    setActionMenuAnchorEl(event.currentTarget)
    setSelectedTestId(testId)
  }

  const handleActionMenuClose = () => {
    setActionMenuAnchorEl(null)
    setSelectedTestId(null)
  }

  const handleDeleteTest = () => {
    if (selectedTestId) {
      console.log('Eliminando test con ID:', selectedTestId)
      handleActionMenuClose()
    }
  }

  return (
    <div className="flex flex-col gap-1 w-full my-6">
      <span className="text-[20px] text-[#4F4F4F] font-bold mt-2">Test</span>

      {/* Test Categories Tabs */}
      <div className="flex mt-2">
        {testCategories.map((category, index) => (
          <React.Fragment key={category.id}>
            <span
              onClick={() => !isReadOnly && setActiveTab(category.id)}
              className={`px-0 py-2 text-[16px] font-medium transition-colors shadow-none ${isReadOnly ? 'cursor-default' : 'cursor-pointer'} ${
                activeTab === category.id
                  ? 'text-[#B80F15]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {category.label} ({getCategoryCount(category.id)})
            </span>
            {index < testCategories.length - 1 && (
              <div className="w-px bg-gray-200 mx-4"></div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Tests List */}
      <div className="space-y-0">
        <AnimatePresence mode="wait">
          {filteredTests.length > 0 && (
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {filteredTests.map((test) => (
                <div key={test.id_test} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-b-0">
                  {/* Date */}
                  <div className="w-22">
                    <span className="text-sm text-[#4F4F4F]">{test.data || '--'}</span>
                  </div>

                  {/* Test Name */}
                  <div className="flex-1 ml-4">
                    <span className="text-sm font-medium text-[#4F4F4F]">{test.nome || '--'}</span>
                  </div>

                  {/* Description */}
                  <div className="flex-1 ml-4">
                    <span className="text-sm text-[#4F4F4F]">{test.descrizione || '--'}</span>
                  </div>

                  {/* Published Status */}
                  <div className="flex items-center gap-2 ml-4">
                    <EdSwitch
                      id={`published-${test.id_test}`}
                      label="Pubblicato"
                      value={test.pubblicato === '1'}
                      onChange={() => {}}
                      disabled={isReadOnly}
                    />
                  </div>

                  {/* Action Icons */}
                  {!isReadOnly && (
                    <div className="flex items-center gap-0 ml-4">
                      <span
                        className="p-1 hover:bg-gray-100 rounded transition-colors cursor-pointer"
                        onClick={() => handleEditTest(test)}
                      >
                        <i className="ri-pencil-fill text-gray-500 text-xl"></i>
                      </span>
                      <span
                        className="p-1 hover:bg-gray-100 rounded transition-colors cursor-pointer"
                        onClick={(e) => handleActionMenuOpen(e, test.id_test || '')}
                      >
                        <i className="ri-more-2-fill text-gray-500 text-xl"></i>
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Add New Test Button */}
      {!isReadOnly && (
        <div className="mt-0">
          <span
            onClick={handleCreateTest}
            className="text-[#B80F15] text-sm font-medium hover:text-red-700 transition-colors cursor-pointer"
          >
            + Aggiungi nuovo test
          </span>
        </div>
      )}

      {/* Modal */}
      <AddTestModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        mode={modalMode}
        testData={selectedTestData}
        initialTab={modalInitialTab}
      />

      {/* Action Menu */}
      <MuiMenu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={handleActionMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 150,
            boxShadow: '1px 1px 15px 0px rgba(0, 0, 0, 0.1)',
            borderRadius: '8px',
          },
        }}
      >
        <MenuItem onClick={handleDeleteTest} sx={{ color: '#000000' }}>
          Elimina
        </MenuItem>
      </MuiMenu>
    </div>
  )
}

export default TestSection
